import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faArrowLeft,
  faArrowRight,
  faBan,
  faCheck,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { useDebounce } from 'use-debounce';

import { scopeBannerAmber } from 'lib/css/colors';
import {
  BRAND_SKU_MAPPER_MFR_TO_DP_COUNTS,
  BRAND_SKU_MAPPER_MFR_TO_DP_FILTER_OPTIONS,
  BRAND_SKU_MAPPER_MFR_TO_DP_PRODUCT_CANDIDATES,
  BRAND_SKU_MAPPER_MFR_TO_DP_PRODUCTS,
  BRAND_SKU_MAPPER_MFR_TO_DP_REVIEW_CANDIDATE,
  BRAND_SKU_MAPPER_MFR_TO_DP_UNAPPROVE_CANDIDATE,
  BRAND_SKU_MAPPER_MFR_TO_DP_START_RUN,
  BRAND_SKU_MAPPER_MFR_TO_DP_CANCEL_RUN,
  BRAND_SKU_MAPPER_MFR_TO_DP_INFLUENCE_EXPORT,
  getURL,
  getURLWithSearchParams,
} from 'lib/networking/endpoints';
import {
  BRAND_SKU_MAPPER_MFR_TO_DP_REVIEW_STATUS_TABS,
  BrandSkuMapperMfrToDpReviewAction,
  BrandSkuMapperMfrToDpReviewStatus,
  BrandSkuMapperMfrToDpRunRequestField,
  BrandSkuMapperMfrToDpRunStatus,
} from 'lib/enums/BrandSkuMapperMfrToDpEnums';
import { httpGet, httpPost } from 'lib/networking/http';
import { selectStyles } from 'lib/css/selectStyles';
import CancelRunButton from 'components/brand_sku_mapper_shared/CancelRunButton';
import useCancelRun from 'components/brand_sku_mapper_shared/useCancelRun';
import ChannelBadges from 'components/brand_sku_mapper_mfr_to_dp/ChannelBadges';
import CandidateCard from 'components/brand_sku_mapper_mfr_to_dp/CandidateCard';
import { ProductPanel } from 'components/brand_sku_mapper_mfr_to_dp/ProductPanel';
import { ZoomImageGallery } from 'components/brand_sku_mapper_shared/ZoomImageGallery';
import { SIDE } from 'components/brand_sku_mapper_mfr_to_dp/constants';
import {
  MISMATCH_SIGNAL_LABELS,
  candBrandLine,
  candVendorSkuLine,
  pct,
  tierColor,
} from 'components/brand_sku_mapper_mfr_to_dp/cardHelpers';
import { styles } from 'components/brand_sku_mapper_mfr_to_dp/sharedStyles';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import Toggle from 'components/brand_sku_mapper_mfr_to_dp/Toggle';
import SwalDialog, { confirmRichAction, SwalToast } from 'lib/utils/toast';
import TabBar from 'components/shared/TabBar';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const PAGE_SIZE = 20;
const MAX_CANDIDATES = 25;
const FILTER_OPTION_DEBOUNCE_MS = 400;
const MIN_FILTER_QUERY_LENGTH = 2;

// react-select's menu defaults to a low zIndex; lift it above the candidate cards.
const filterSelectStyles = {
  ...selectStyles,
  menu: base => ({ ...base, zIndex: 20 }),
};

// Type-to-search options for one dropdown field, cross-filtered by the others.
function fetchFilterOptions(field, query, crossFilters) {
  const params = { field };
  const trimmed = (query || '').trim();
  if (trimmed.length >= MIN_FILTER_QUERY_LENGTH) params.query = trimmed;
  if (crossFilters.brandId) params.brand_id = crossFilters.brandId;
  if (crossFilters.manufacturerId) params.manufacturer_id = crossFilters.manufacturerId;
  if (crossFilters.distributorId) params.distributor_id = crossFilters.distributorId;
  if (crossFilters.influenceOnly) params.influence_only = crossFilters.influenceOnly;
  return httpGet(getURLWithSearchParams(BRAND_SKU_MAPPER_MFR_TO_DP_FILTER_OPTIONS, params)).then(
    // Distributor options are a searchable catalog list with no per-product count (aggregating it
    // is too slow at scale); brand/manufacturer keep their scored counts.
    res =>
      (res.data?.options || []).map(o => ({
        value: o.id,
        label: field === 'distributor' ? o.name : `${o.name} (${o.count})`,
      })),
  );
}

// Option labels are "Name (count)"; strip the count for the run confirm dialog.
function optionName(option) {
  return option ? option.label.replace(/\s*\(\d+\)$/, '') : null;
}

// Escape catalog-sourced names before interpolating into confirmRichAction HTML.
const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, c => HTML_ENTITIES[c]);
}

// Debounce loadOptions so a fast typist doesn't fire one query per keystroke.
function useDebouncedLoadOptions(loader, label) {
  const timerRef = useRef(null);
  const pendingResolveRef = useRef(null);
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingResolveRef.current) pendingResolveRef.current([]);
    },
    [],
  );
  return useCallback(
    inputValue =>
      new Promise(resolve => {
        clearTimeout(timerRef.current);
        if (pendingResolveRef.current) pendingResolveRef.current([]);
        pendingResolveRef.current = resolve;
        timerRef.current = setTimeout(() => {
          pendingResolveRef.current = null;
          loader(inputValue)
            .then(resolve)
            .catch(() => {
              SwalToast('error', `Could not load ${label} options.`);
              resolve([]);
            });
        }, FILTER_OPTION_DEBOUNCE_MS);
      }),
    [loader, label],
  );
}

// What justifies overriding a manufacturer mismatch (mirrors V2's signal).

function BrandSkuMapperMfrToDpReviewView() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState(BrandSkuMapperMfrToDpReviewStatus.PENDING);
  const [searchInput, setSearchInput] = useState('');
  const [query] = useDebounce(searchInput, 400);
  // Binary Brand toggle: true = MFR products WITH a brand, false = WITHOUT.
  const [hasBrand, setHasBrand] = useState(true);
  const [pageIdx, setPageIdx] = useState(0);
  // Multi-select: many distributor products can be the same mfr product.
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  // Vendor ids hidden from the panel while an approve/un-approve is in flight.
  const [optimisticHidden, setOptimisticHidden] = useState([]);
  const [zoomedVendorId, setZoomedVendorId] = useState(null);
  const [zoomedProductOpen, setZoomedProductOpen] = useState(false);
  const [reviewingAction, setReviewingAction] = useState(null);
  const [runLimit, setRunLimit] = useState('');
  const [jumpValue, setJumpValue] = useState('');
  const [tabPositions, setTabPositions] = useState({});
  // Cross-filtered scope dropdowns (brand/manufacturer -> MFR product, distributor -> DP).
  const [brandOption, setBrandOption] = useState(null);
  const [manufacturerOption, setManufacturerOption] = useState(null);
  const [distributorOption, setDistributorOption] = useState(null);
  const [influenceOnly, setInfluenceOnly] = useState(false);
  const [campaignOnly, setCampaignOnly] = useState(false); // Influence+ sub-filter
  const brandId = brandOption?.value || '';
  const manufacturerId = manufacturerOption?.value || '';
  const distributorId = distributorOption?.value || '';
  const reviewedIdRef = useRef(null);

  // Distributor filters the product list (skip mfr products with no DP from it) AND the candidate
  // cards below (client-side), so it belongs in the list request + the page-reset deps.
  useEffect(() => {
    setPage(0);
  }, [query, hasBrand, brandId, manufacturerId, influenceOnly, campaignOnly, distributorId]);

  const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
  if (status) params.status = status;
  if (query) params.query = query;
  params.has_brand = hasBrand;
  if (brandId) params.brand_id = brandId;
  if (manufacturerId) params.manufacturer_id = manufacturerId;
  params.influence_only = influenceOnly; // normal vs Influence+ rows are a separate set
  if (influenceOnly && campaignOnly) params.campaign_only = true; // Influence+ sub-filter
  if (distributorId) params.distributor_id = distributorId;

  const { data, loading, refetch, silentRefetch } = useGet(
    BRAND_SKU_MAPPER_MFR_TO_DP_PRODUCTS,
    params,
  );
  const countsParams = { influence_only: influenceOnly };
  if (influenceOnly && campaignOnly) countsParams.campaign_only = true; // campaign run scope
  if (brandId) countsParams.brand_id = brandId;
  if (manufacturerId) countsParams.manufacturer_id = manufacturerId;
  const { data: counts, refetch: refetchCounts } = useGet(
    BRAND_SKU_MAPPER_MFR_TO_DP_COUNTS,
    countsParams,
  );
  const allCount = counts?.all_count ?? null;
  const newCount = counts?.new_count ?? null;

  const loadBrandOptions = useDebouncedLoadOptions(
    useCallback(
      q => fetchFilterOptions('brand', q, { manufacturerId, distributorId, influenceOnly }),
      [manufacturerId, distributorId, influenceOnly],
    ),
    'brand',
  );
  const loadManufacturerOptions = useDebouncedLoadOptions(
    useCallback(
      q => fetchFilterOptions('manufacturer', q, { brandId, distributorId, influenceOnly }),
      [brandId, distributorId, influenceOnly],
    ),
    'manufacturer',
  );
  const loadDistributorOptions = useDebouncedLoadOptions(
    useCallback(
      q => fetchFilterOptions('distributor', q, { brandId, manufacturerId, influenceOnly }),
      [brandId, manufacturerId, influenceOnly],
    ),
    'distributor',
  );

  const latestRun = data?.latest_run || null;
  const candidates = useMemo(() => data?.candidates || [], [data]);
  const total = data?.count || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const countsByStatus = data?.counts_by_status || {};

  const runIsRunning = latestRun?.status === BrandSkuMapperMfrToDpRunStatus.RUNNING;
  const runIsPending = latestRun?.status === BrandSkuMapperMfrToDpRunStatus.PENDING;
  const processedCount = latestRun?.processed_count || 0;
  const totalCount = latestRun?.total_products || 0;
  const runDone = latestRun && !runIsRunning && !runIsPending;
  const failedChunks = runDone ? latestRun.run_metadata?.failed_chunks : 0;
  const denseOnly = runDone ? latestRun.run_metadata?.sparse_disabled : false;

  useEffect(() => {
    if (!runIsRunning && !runIsPending) return undefined;
    const id = setInterval(silentRefetch, 4000);
    return () => clearInterval(id);
  }, [runIsRunning, runIsPending, silentRefetch]);

  useEffect(() => {
    setPageIdx(0);
  }, [query, hasBrand, brandId, manufacturerId, influenceOnly, campaignOnly, distributorId]);

  const safeIdx = Math.min(pageIdx, Math.max(candidates.length - 1, 0));
  const candidate = candidates[safeIdx];
  const globalIdx = page * PAGE_SIZE + safeIdx;
  const gpmId = candidate?.global_product_manufacturer_id || null;

  useEffect(() => {
    setSelectedVendorIds([]); // every product starts with a clean selection
    setZoomedVendorId(null);
    setZoomedProductOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  const candUrl = gpmId
    ? getURL(BRAND_SKU_MAPPER_MFR_TO_DP_PRODUCT_CANDIDATES, {
        global_product_manufacturer_id: gpmId,
      })
    : BRAND_SKU_MAPPER_MFR_TO_DP_PRODUCT_CANDIDATES;
  const {
    data: candData,
    loading: candLoading,
    refetch: refetchCand,
  } = useGet(candUrl, { influence_only: influenceOnly }, !gpmId);

  // Once the panel reloads with real server state, drop the optimistic hides.
  useEffect(() => {
    setOptimisticHidden([]);
  }, [candData]);

  const allRankedCandidates = useMemo(
    () => (candData?.candidates || []).slice(0, MAX_CANDIDATES),
    [candData],
  );
  const approvedList = useMemo(() => candData?.approved || [], [candData]);
  const notHidden = c => !optimisticHidden.includes(c.vendor_product_id);
  // Distributor filter is applied to the candidate cards here (rank is preserved -- each card
  // keeps its own `rank`). A product with no DP from the selected distributor shows 0 cards.
  const distFilterId = distributorId ? Number(distributorId) : null;
  const matchesDistributor = c =>
    !distFilterId || (c.vendor_product && c.vendor_product.distributor_id === distFilterId);
  const pendingCards = allRankedCandidates.filter(notHidden).filter(matchesDistributor);
  const approvedCards = approvedList.filter(notHidden).filter(matchesDistributor);
  const hasCandidates = pendingCards.length > 0;
  const hasApproved = approvedCards.length > 0; // visible-for-this-distributor (drives display)
  // Whether the product has ANY approval, regardless of the distributor filter -- No Match applies
  // to the whole product, so it must gate on the real state, not the filtered cards (else it looks
  // enabled while the server rejects it).
  const hasAnyApproved = approvedList.filter(notHidden).length > 0;
  // Section visibility: Pending -> candidates, Approved -> approvals, else both.
  const isApprovedTab = status === BrandSkuMapperMfrToDpReviewStatus.APPROVED;
  const isPendingTab = status === BrandSkuMapperMfrToDpReviewStatus.PENDING;
  const showApprovedSection = hasApproved && !isPendingTab;
  const showCandidatesSection = hasCandidates && !isApprovedTab;
  const zoomedCandidate =
    zoomedVendorId != null
      ? [...pendingCards, ...approvedCards].find(c => c.vendor_product_id === zoomedVendorId) ||
        null
      : null;

  useEffect(() => {
    if (zoomedVendorId != null && !zoomedCandidate) setZoomedVendorId(null);
  }, [zoomedVendorId, zoomedCandidate]);

  const atFirst = page === 0 && safeIdx === 0;
  const atLast = page >= totalPages - 1 && safeIdx >= candidates.length - 1;

  const goPrevProduct = useCallback(() => {
    if (safeIdx > 0) setPageIdx(safeIdx - 1);
    else if (page > 0) {
      setPage(page - 1);
      setPageIdx(PAGE_SIZE - 1);
    }
  }, [page, safeIdx]);

  const goNextProduct = useCallback(() => {
    if (safeIdx < candidates.length - 1) setPageIdx(safeIdx + 1);
    else if (page < totalPages - 1) {
      setPage(page + 1);
      setPageIdx(0);
    }
  }, [candidates.length, page, safeIdx, totalPages]);

  const goToGlobalIndex = useCallback(idx => {
    const clamped = Math.max(0, idx);
    setPage(Math.floor(clamped / PAGE_SIZE));
    setPageIdx(clamped % PAGE_SIZE);
  }, []);

  useEffect(() => {
    setJumpValue(String(globalIdx + 1));
  }, [globalIdx]);

  const jumpToProduct = useCallback(() => {
    const n = Number(jumpValue);
    if (Number.isInteger(n) && n >= 1 && n <= total) goToGlobalIndex(n - 1);
    else setJumpValue(String(globalIdx + 1));
  }, [jumpValue, total, globalIdx, goToGlobalIndex]);

  const toggleSelected = useCallback(
    c => {
      const id = c.vendor_product_id;
      const doToggle = () =>
        setSelectedVendorIds(prev =>
          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
        );
      // Gate only when newly selecting a manufacturer-mismatched DP (reviewer override, like V2);
      // once overridden here, approve fires without re-warning.
      if (!selectedVendorIds.includes(id) && c.manufacturer_mismatch) {
        const evidence = c.mismatch_signal
          ? `Supporting ${
              MISMATCH_SIGNAL_LABELS[c.mismatch_signal] || c.mismatch_signal
            } evidence exists, but the DP's manufacturer does not match this product.`
          : "The DP's manufacturer does not match this product, with no GTIN/MPN/brand evidence.";
        confirmRichAction(
          doToggle,
          `<div>${evidence}</div><div style="margin-top:6px;">Select it anyway?</div>`,
          'Manufacturer mismatch',
          'Override & select',
        );
        return;
      }
      doToggle();
    },
    [selectedVendorIds],
  );

  const review = useCallback(
    (action, pickedVendorProductIds = []) => {
      if (!candidate || reviewingAction !== null) return;
      setReviewingAction(action);
      // Optimistic: hide approved cards + clear selection immediately.
      if (action === BrandSkuMapperMfrToDpReviewAction.APPROVE && pickedVendorProductIds.length) {
        setOptimisticHidden(prev => [...prev, ...pickedVendorProductIds]);
      }
      setSelectedVendorIds([]);
      httpPost(
        getURL(BRAND_SKU_MAPPER_MFR_TO_DP_REVIEW_CANDIDATE, { candidate_id: candidate.id }),
        {
          action,
          picked_vendor_product_ids: pickedVendorProductIds,
        },
      )
        .then(() => {
          if (!atLast) reviewedIdRef.current = candidate.id;
          refetchCand();
          refetch();
        })
        .catch(err => {
          setOptimisticHidden([]); // roll back the optimistic hide on failure
          const body = err?.response?.data;
          SwalDialog('error', body?.message || 'Failed to update candidate.', 'Error');
        })
        .finally(() => setReviewingAction(null));
    },
    [candidate, reviewingAction, atLast, refetch, refetchCand],
  );

  const unapprove = useCallback(
    vendorProductId => {
      if (!candidate || reviewingAction !== null) return;
      setReviewingAction('unapprove');
      setOptimisticHidden(prev => [...prev, vendorProductId]); // drop it from the panel now
      httpPost(
        getURL(BRAND_SKU_MAPPER_MFR_TO_DP_UNAPPROVE_CANDIDATE, { candidate_id: candidate.id }),
        { vendor_product_id: vendorProductId },
      )
        .then(() => {
          refetchCand();
          refetch();
        })
        .catch(err => {
          setOptimisticHidden([]); // roll back on failure
          const body = err?.response?.data;
          SwalDialog('error', body?.message || 'Failed to un-approve.', 'Error');
        })
        .finally(() => setReviewingAction(null));
    },
    [candidate, reviewingAction, refetch, refetchCand],
  );

  useEffect(() => {
    if (!reviewedIdRef.current) return;
    const stillPresent = candidates.some(c => c.id === reviewedIdRef.current);
    reviewedIdRef.current = null;
    if (stillPresent) goNextProduct();
  }, [candidates, goNextProduct]);

  useEffect(() => {
    const isTyping = () => {
      const tag = document.activeElement?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
    };
    const handler = e => {
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        isTyping() ||
        reviewingAction !== null ||
        zoomedVendorId != null ||
        zoomedProductOpen
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrevProduct();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNextProduct();
      } else if (!candidate) {
        // nothing
      } else if (e.key === 'a' || e.key === 'A') {
        if (selectedVendorIds.length) {
          e.preventDefault();
          review(BrandSkuMapperMfrToDpReviewAction.APPROVE, selectedVendorIds);
        }
      } else if (e.key === 'n' || e.key === 'N') {
        if (
          !hasAnyApproved &&
          candidate.review_status !== BrandSkuMapperMfrToDpReviewStatus.NO_MATCH
        ) {
          e.preventDefault();
          review(BrandSkuMapperMfrToDpReviewAction.NO_MATCH);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    candidate,
    goNextProduct,
    goPrevProduct,
    review,
    reviewingAction,
    selectedVendorIds,
    zoomedVendorId,
    zoomedProductOpen,
    hasAnyApproved,
  ]);

  const { postData: dispatchRun, loading: dispatchingRun } = usePost(
    BRAND_SKU_MAPPER_MFR_TO_DP_START_RUN,
    () => {
      SwalToast('success', 'Run started.');
      refetch();
      refetchCounts();
    },
    err => SwalToast('error', err?.response?.data?.message || 'Could not start a run.'),
  );

  const { cancelRun, cancelling: cancellingRun } = useCancelRun(
    BRAND_SKU_MAPPER_MFR_TO_DP_CANCEL_RUN,
    () => {
      refetch();
      refetchCounts();
    },
  );

  // Influence+ only: zip of 3 CSVs (stats / mapped / unmapped) built async -> File Exports.
  const { postData: dispatchExport, loading: exporting } = usePost(
    BRAND_SKU_MAPPER_MFR_TO_DP_INFLUENCE_EXPORT,
    () =>
      SwalDialog(
        'success',
        'The Influence+ CSV report is being generated. Download it from the File Exports page in a few minutes.',
        'Export started',
        'center',
      ),
    err => SwalToast('error', err?.response?.data?.message || 'Could not start the export.'),
  );

  const startRun = useCallback(
    remainingOnly => {
      const parsedLimit = Number(runLimit);
      const hasLimit = runLimit !== '' && Number.isInteger(parsedLimit) && parsedLimit > 0;
      const poolCount = remainingOnly ? newCount : allCount;
      const poolText = poolCount != null ? `${poolCount.toLocaleString()}` : 'the';
      const confirmTitle = remainingOnly ? 'Run for new products only?' : 'Run for all products?';
      const limStr = `<strong>${parsedLimit.toLocaleString()}</strong>`;
      // Make the selected scope explicit so a scoped run doesn't read as a full run.
      // Distributor is a review-view filter only, never a run scope -- not part of the banner.
      const scopeParts = [
        influenceOnly &&
          (campaignOnly ? 'Influence+ campaign-enabled products' : 'Influence+ products'),
        brandOption && `brand "${escapeHtml(optionName(brandOption))}"`,
        manufacturerOption && `manufacturer "${escapeHtml(optionName(manufacturerOption))}"`,
      ].filter(Boolean);
      const scopeBannerStyle = `color:${scopeBannerAmber};margin-bottom:6px;`;
      const scopeBanner = scopeParts.length
        ? `<div style="${scopeBannerStyle}"><strong>Scoped to ${scopeParts.join(
            ' + ',
          )}.</strong> Only these will be scored/matched.</div>`
        : '';
      const body = hasLimit
        ? `Score up to ${limStr} of ${poolText} manufacturer products.`
        : remainingOnly
        ? `Only manufacturer products never scored before will be processed (${poolText} now).`
        : `Re-score <strong>every</strong> active manufacturer product ` +
          `(${poolText} now). This can take a while.`;
      const confirmHtml = scopeBanner + body;
      confirmRichAction(
        () =>
          dispatchRun({
            [BrandSkuMapperMfrToDpRunRequestField.REMAINING_ONLY]: remainingOnly,
            [BrandSkuMapperMfrToDpRunRequestField.INFLUENCE_ONLY]: influenceOnly,
            [BrandSkuMapperMfrToDpRunRequestField.CAMPAIGN_ONLY]: influenceOnly && campaignOnly,
            ...(hasLimit
              ? { [BrandSkuMapperMfrToDpRunRequestField.MAX_PRODUCTS]: parsedLimit }
              : {}),
            ...(brandId ? { [BrandSkuMapperMfrToDpRunRequestField.BRAND_ID]: brandId } : {}),
            ...(manufacturerId
              ? { [BrandSkuMapperMfrToDpRunRequestField.MANUFACTURER_ID]: manufacturerId }
              : {}),
          }),
        confirmHtml,
        confirmTitle,
        remainingOnly ? 'Yes, run for new only' : 'Yes, run for all',
      );
    },
    [
      dispatchRun,
      runLimit,
      allCount,
      newCount,
      brandId,
      manufacturerId,
      brandOption,
      manufacturerOption,
      influenceOnly,
      campaignOnly,
    ],
  );

  // One card. mode='approved': un-approve, not selectable. mode='pending': selectable.
  const renderCard = (c, mode) => (
    <CandidateCard
      key={c.vendor_product_id}
      candidate={c}
      mode={mode}
      isSelected={mode !== 'approved' && selectedVendorIds.includes(c.vendor_product_id)}
      reviewingAction={reviewingAction}
      onToggleSelect={toggleSelected}
      onUnapprove={unapprove}
      onZoom={setZoomedVendorId}
    />
  );

  return (
    <>
      <Container className={css(styles.container)}>
        <div className={css(styles.headerRow)}>
          <div className={css(styles.headerLeft)}>
            <span className={css(styles.title)}>Brand SKU Mapper — MFR → DP</span>
            {runDone && (
              <span className={css(styles.headerMeta)}>
                {`${latestRun.matched_count || 0}/${latestRun.total_products || 0} matched`}
              </span>
            )}
            {failedChunks ? (
              <span
                className={css(styles.runWarning)}
                title="Some chunks failed; those products weren't scored. Run 'New Only' to finish."
              >
                {`⚠ ${failedChunks} chunk(s) failed — run 'New Only' to finish`}
              </span>
            ) : null}
            {denseOnly ? (
              <span className={css(styles.runWarning)} title="Sparse disabled — dense-only scores.">
                ⚠ dense-only (sparse unavailable)
              </span>
            ) : null}
            {(runIsRunning || runIsPending) && (
              <span className={css(styles.runningIndicator)}>
                <Spinner animation="border" size="sm" className="mr-1" />
                {runIsPending ? 'Queued…' : `Scoring ${processedCount}/${totalCount}…`}
                <CancelRunButton cancelling={cancellingRun} onClick={() => cancelRun()} />
              </span>
            )}
            {runIsRunning && totalCount > 0 && (
              <div className={css(styles.progressTrack)}>
                <div
                  className={css(styles.progressFill)}
                  style={{ width: `${Math.min(100, (100 * processedCount) / totalCount)}%` }}
                />
              </div>
            )}
            {!runIsRunning && !runIsPending && (
              <>
                <Form.Control
                  type="number"
                  min="1"
                  placeholder="Limit"
                  value={runLimit}
                  onChange={e => setRunLimit(e.target.value)}
                  className={css(styles.limitInput)}
                  title="Optional: cap how many manufacturer products this run scores"
                />
                <Button
                  size="sm"
                  className={css(styles.dispatchBtn)}
                  onClick={() => startRun(false)}
                  disabled={dispatchingRun}
                >
                  Run for All
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className={css(styles.newRunBtn)}
                  onClick={() => startRun(true)}
                  disabled={dispatchingRun}
                >
                  Run for New Only
                </Button>
                {(allCount != null || newCount != null) && (
                  <span className={css(styles.eligibleCountText)}>
                    {allCount != null ? `${allCount.toLocaleString()} total` : '—'}
                    {' · '}
                    {newCount != null ? `${newCount.toLocaleString()} new` : '—'}
                  </span>
                )}
              </>
            )}
          </div>
          <Badge className={css(styles.totalBadge)}>{total.toLocaleString()} products</Badge>
        </div>

        <div className={css(styles.tabRow)}>
          <TabBar
            tabs={BRAND_SKU_MAPPER_MFR_TO_DP_REVIEW_STATUS_TABS.map(t => ({
              key: t.key,
              label: t.label,
              count: t.key && countsByStatus[t.key] != null ? countsByStatus[t.key] : undefined,
            }))}
            activeKey={status}
            onSelect={key => {
              if (key === status) return;
              setTabPositions(prev => ({ ...prev, [status]: globalIdx }));
              const saved = tabPositions[key] ?? 0;
              const cap =
                countsByStatus[key] != null ? Math.max(countsByStatus[key] - 1, 0) : saved;
              setStatus(key);
              goToGlobalIndex(Math.min(saved, cap));
            }}
          />
          <div className={css(styles.tabRowRight)}>
            <Toggle
              checked={influenceOnly}
              onToggle={() =>
                setInfluenceOnly(p => {
                  if (p) setCampaignOnly(false); // leaving Influence+ clears the sub-filter
                  return !p;
                })
              }
              label="Influence+"
            />
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                visibility: influenceOnly ? 'visible' : 'hidden',
              }}
            >
              <Toggle
                checked={campaignOnly}
                onToggle={() => setCampaignOnly(p => !p)}
                label="Campaign only"
              />
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              className={css(styles.exportBtn)}
              style={{ visibility: influenceOnly ? 'visible' : 'hidden' }}
              onClick={() => dispatchExport({})}
              disabled={exporting || !influenceOnly}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
        </div>

        <Row className="mt-3 align-items-center flex-wrap g-2">
          <Col md={3}>
            <Form.Control
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search manufacturer product name or SKU"
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`brand-${manufacturerId}-${distributorId}-${influenceOnly}`}
              isClearable
              defaultOptions
              loadOptions={loadBrandOptions}
              value={brandOption}
              onChange={opt => {
                setBrandOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by brand"
              noOptionsMessage={() => 'No matching brands'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`manufacturer-${brandId}-${distributorId}-${influenceOnly}`}
              isClearable
              defaultOptions
              loadOptions={loadManufacturerOptions}
              value={manufacturerOption}
              onChange={opt => {
                setManufacturerOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by manufacturer"
              noOptionsMessage={() => 'No matching manufacturers'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`distributor-${brandId}-${manufacturerId}-${influenceOnly}`}
              isClearable
              defaultOptions
              loadOptions={loadDistributorOptions}
              value={distributorOption}
              onChange={opt => {
                setDistributorOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by distributor"
              noOptionsMessage={() => 'No matching distributors'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md="auto" className="d-flex align-items-center">
            <Toggle
              checked={hasBrand}
              onToggle={() => {
                setHasBrand(p => !p);
                setPage(0);
              }}
              label="Brand"
            />
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : !candidate ? (
          <div className="text-center text-muted p-5">No products to review.</div>
        ) : (
          <div className={css(styles.reviewWrapper)}>
            <div className={css(styles.navRow)}>
              <Button
                onClick={goPrevProduct}
                disabled={atFirst || reviewingAction !== null}
                className={css(styles.navBtn)}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Prev product
              </Button>
              <div className={css(styles.positionText)}>
                {total > 0 ? (
                  <>
                    <Form.Control
                      type="number"
                      min="1"
                      max={total}
                      value={jumpValue}
                      disabled={reviewingAction !== null}
                      onChange={e => setJumpValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                      onBlur={jumpToProduct}
                      className={css(styles.jumpInput)}
                      title="Jump to product number"
                    />
                    {` of ${total.toLocaleString()}`}
                  </>
                ) : (
                  '—'
                )}
              </div>
              <Button
                onClick={goNextProduct}
                disabled={atLast || reviewingAction !== null}
                className={css(styles.navBtn)}
              >
                Next product
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>

            {candidate.review_status &&
              candidate.review_status !== BrandSkuMapperMfrToDpReviewStatus.PENDING && (
                <div className={css(styles.statusBanner)}>
                  {candidate.review_status}
                  {candidate.reviewed_by ? ` · by ${candidate.reviewed_by}` : ''}
                </div>
              )}

            {showCandidatesSection && (
              <>
                <Row className={css(styles.actionsRow)}>
                  <Col xs={6}>
                    <Button
                      block
                      className={css(styles.approveBtn)}
                      onClick={() =>
                        review(BrandSkuMapperMfrToDpReviewAction.APPROVE, selectedVendorIds)
                      }
                      disabled={reviewingAction !== null || selectedVendorIds.length === 0}
                    >
                      {reviewingAction === BrandSkuMapperMfrToDpReviewAction.APPROVE ? (
                        <Spinner animation="border" size="sm" className="mr-2" />
                      ) : (
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                      )}
                      {selectedVendorIds.length > 1
                        ? `Approve ${selectedVendorIds.length} selected`
                        : 'Approve selected'}
                    </Button>
                  </Col>
                  <Col xs={6}>
                    <Button
                      block
                      className={css(styles.neutralBtn)}
                      onClick={() => review(BrandSkuMapperMfrToDpReviewAction.NO_MATCH)}
                      disabled={
                        reviewingAction !== null ||
                        hasAnyApproved ||
                        candidate.review_status === BrandSkuMapperMfrToDpReviewStatus.NO_MATCH
                      }
                    >
                      {reviewingAction === BrandSkuMapperMfrToDpReviewAction.NO_MATCH ? (
                        <Spinner animation="border" size="sm" className="mr-2" />
                      ) : (
                        <FontAwesomeIcon icon={faBan} className="mr-2" />
                      )}
                      No match exists
                    </Button>
                  </Col>
                </Row>
                {selectedVendorIds.length === 0 && (
                  <div className={css(styles.hintText)}>
                    Select one or more candidate cards below to enable “Approve” — many distributors
                    can sell the same manufacturer product. Nothing is mapped until you approve.
                  </div>
                )}
              </>
            )}

            <Row className="mt-3">
              <Col md={4}>
                <ProductPanel
                  product={candidate.manufacturer_product}
                  side={SIDE.MANUFACTURER}
                  label="Manufacturer product to map"
                  zoomOpen={zoomedProductOpen}
                  onZoomOpenChange={setZoomedProductOpen}
                />
              </Col>
              <Col md={8}>
                {candLoading ? (
                  <LoadingSpinner />
                ) : !showApprovedSection && !showCandidatesSection ? (
                  <div className={css(styles.mutedText) + ' p-4 text-center'}>
                    {distFilterId
                      ? 'No candidates from the selected distributor for this manufacturer product.'
                      : 'No distributor products for this manufacturer product.'}
                  </div>
                ) : (
                  <div className={css(styles.candScrollArea)}>
                    {showApprovedSection && (
                      <>
                        <div className={css(styles.candHeader)}>
                          <span className={css(styles.candHeaderText)}>
                            {`${approvedCards.length} approved`}
                          </span>
                        </div>
                        <div className={css(styles.candGrid)}>
                          {approvedCards.map(c => renderCard(c, 'approved'))}
                        </div>
                      </>
                    )}
                    {showCandidatesSection && (
                      <>
                        <div
                          className={css(styles.candHeader)}
                          style={showApprovedSection ? { marginTop: 16 } : undefined}
                        >
                          <span className={css(styles.candHeaderText)}>
                            {`${showApprovedSection ? 'Remaining candidates' : 'Candidates'}: ${
                              pendingCards.length
                            }`}
                          </span>
                        </div>
                        <div className={css(styles.candGrid)}>
                          {pendingCards.map(c => renderCard(c, 'pending'))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}
      </Container>
      {zoomedCandidate && (
        <Modal
          show
          onHide={() => setZoomedVendorId(null)}
          centered
          dialogClassName={css(styles.zoomModalDialog)}
        >
          <Modal.Body className={css(styles.zoomModalBody)}>
            <button
              type="button"
              className={css(styles.zoomCloseButton)}
              onClick={() => setZoomedVendorId(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ZoomImageGallery
              thumbnailUrl={(zoomedCandidate.vendor_product || {}).thumbnail_url}
              imageUrls={(zoomedCandidate.vendor_product || {}).image_urls}
            />
            <div className={css(styles.zoomDetails)}>
              <div className={css(styles.candName, styles.zoomName)}>
                {(zoomedCandidate.vendor_product || {}).name ||
                  `DP #${zoomedCandidate.vendor_product_id}`}
              </div>
              <span
                className={css(styles.tierPillFloating, styles.zoomTierPill)}
                style={{ backgroundColor: tierColor(zoomedCandidate.tier) }}
              >
                {pct(zoomedCandidate.calibrated_confidence)} · {zoomedCandidate.tier || '—'}
              </span>
              <div className={css(styles.mutedText)}>
                {`sts:${((zoomedCandidate.vendor_product || {}).status || 'null').toLowerCase()}`}
              </div>
              <div className={css(styles.mutedText)}>
                {candBrandLine(zoomedCandidate.vendor_product || {})}
              </div>
              <div className={css(styles.mutedText)}>
                {(zoomedCandidate.vendor_product || {}).pack_size || '—'}
              </div>
              <div className={css(styles.mutedText)}>
                {candVendorSkuLine(zoomedCandidate.vendor_product || {})}
              </div>
              <div className={css(styles.zoomReasoningRow)}>
                <ChannelBadges channels={zoomedCandidate.channels} />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default BrandSkuMapperMfrToDpReviewView;
